import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Scissors, CheckCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#2B2B2B]/50">Invalid reset link.</p>
          <Link to="/login" className="text-[#5C3A2E] hover:text-[#C6A969] text-sm mt-2 inline-block">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5C3A2E] rounded-2xl mb-5">
            <Scissors className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-4xl text-[#5C3A2E] font-semibold tracking-tight">New Password</h1>
        </div>

        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] p-8 animate-fade-in-up animate-stagger-2">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle className="w-10 h-10 text-[#C6A969] mx-auto mb-3" />
              <h3 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-2">Password updated</h3>
              <p className="text-[#2B2B2B]/50 text-sm mb-4">You can now sign in with your new password.</p>
              <Link to="/login"><Button className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">Sign In</Button></Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div data-testid="reset-error" className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-100">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">New Password</label>
                <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="w-full rounded-xl border-[#EADBC8] bg-white px-4 py-3" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Confirm Password</label>
                <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="w-full rounded-xl border-[#EADBC8] bg-white px-4 py-3" required />
              </div>
              <Button data-testid="reset-submit-button" type="submit" disabled={submitting} className="w-full bg-[#5C3A2E] text-white rounded-full py-3 hover:bg-[#5C3A2E]/90 h-12">
                {submitting ? "Updating..." : "Update Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
